import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { audioPublicUrl, getDb } from './firebase';
import { AuthRequiredError, isPermissionDenied } from './errors';
import type {
  CourseDoc,
  CourseSummary,
  CourseWithTree,
  Level,
  MondaiType,
  OptionOrder,
  QuestionDetail,
  QuestionDoc,
  QuestionListItem,
  SectionDetail,
  SectionDoc,
  SectionWithQuestions,
} from './types';

export interface LevelSummary {
  level: Level;
  available: boolean;
  exam_count: number;
  mondai_count: number;
}

export interface ExamSummary {
  slug: string;
  title_vi: string;
  order_index: number;
}

export interface ExamMondaiItem {
  mondai_type: MondaiType;
  mondai_name_jp: string;
  mondai_name_vi: string;
  courseSlug: string;
  question_count: number;
  options_per_question: number;
}

/**
 * Content reads require auth (Phase 0 rules). Firestore denies anonymous reads with
 * `permission-denied`; translate that into an AuthRequiredError so routes show the friendly
 * sign-in screen instead of a red error. Any other error passes through unchanged.
 */
function rethrowContentError(err: unknown): never {
  if (isPermissionDenied(err)) throw new AuthRequiredError();
  throw err;
}

function mapCourse(id: string, data: Record<string, unknown>): CourseDoc {
  return {
    id,
    slug: data.slug as string,
    level: data.level as CourseDoc['level'],
    mondai_type: data.mondai_type as CourseDoc['mondai_type'],
    mondai_name_jp: (data.mondai_name_jp as string) ?? '',
    mondai_name_vi: (data.mondai_name_vi as string) ?? '',
    title_vi: data.title_vi as string,
    description_vi: (data.description_vi as string | null) ?? null,
    source_videos: (data.source_videos as CourseDoc['source_videos']) ?? [],
    thumbnail_url: (data.thumbnail_url as string | null) ?? null,
    question_count: (data.question_count as number) ?? 0,
    section_count: (data.section_count as number) ?? 0,
    options_per_question: (data.options_per_question as number) ?? 3,
    audio_granularity: (data.audio_granularity as CourseDoc['audio_granularity']) ?? 'section',
    default_reading_time_seconds: (data.default_reading_time_seconds as number | null) ?? null,
  };
}

function mapSection(id: string, data: Record<string, unknown>): SectionDoc {
  return {
    id,
    slug: data.slug as string,
    title_vi: data.title_vi as string,
    order_index: data.order_index as number,
    source_video_id: (data.source_video_id as string) ?? '',
    video_start_seconds: (data.video_start_seconds as number) ?? 0,
    audio_path: data.audio_path as string,
    audio_duration_seconds: (data.audio_duration_seconds as number) ?? 0,
    question_count: (data.question_count as number) ?? 0,
  };
}

function mapQuestion(id: string, data: Record<string, unknown>): QuestionDoc {
  return {
    id,
    question_number: data.question_number as number,
    order_index: data.order_index as number,
    start_seconds: data.start_seconds as number,
    end_seconds: data.end_seconds as number,
    prompt_text: (data.prompt_text as string) ?? '',
    full_script: (data.full_script as string) ?? '',
    correct_option_order: Number(data.correct_option_order) as OptionOrder,
    needs_review: Boolean(data.needs_review),
    explanation: (data.explanation as QuestionDoc['explanation']) ?? null,
    options: ((data.options as QuestionDoc['options']) ?? []).map((o) => ({
      order: Number(o.order) as OptionOrder,
      text: o.text,
    })),
    audio_path: (data.audio_path as string | null) ?? null,
    audio_duration_seconds: (data.audio_duration_seconds as number | null) ?? null,
    image_path: (data.image_path as string | null) ?? null,
    reading_time_seconds: (data.reading_time_seconds as number | null) ?? null,
  };
}

export async function getCoursesList(): Promise<CourseSummary[]> {
  try {
    const db = getDb();
    const snap = await getDocs(collection(db, 'courses'));
    return snap.docs.map((d) => mapCourse(d.id, d.data()));
  } catch (e) {
    rethrowContentError(e);
  }
}

const ALL_LEVELS: Level[] = ['N1', 'N2', 'N3', 'N4', 'N5'];

export async function getLevelsList(): Promise<LevelSummary[]> {
  const courses = await getCoursesList();
  const byLevel = new Map<Level, CourseDoc[]>();
  for (const c of courses) {
    if (!byLevel.has(c.level)) byLevel.set(c.level, []);
    byLevel.get(c.level)!.push(c);
  }
  return ALL_LEVELS.map((level) => {
    const list = byLevel.get(level) ?? [];
    const examCount = list.length > 0 ? Math.max(...list.map((c) => c.section_count)) : 0;
    return {
      level,
      available: list.length > 0,
      exam_count: examCount,
      mondai_count: list.length,
    };
  });
}

function courseSlugFor(level: Level, mondai: MondaiType): string {
  return `${level.toLowerCase()}-mondai-${mondai}`;
}

export async function getExamsForLevel(level: Level): Promise<ExamSummary[]> {
  try {
    const db = getDb();
    const mondaiNums: MondaiType[] = [1, 2, 3, 4, 5];
    const sectionSnaps = await Promise.all(
      mondaiNums.map((mondai) => {
        const courseRef = doc(db, 'courses', courseSlugFor(level, mondai));
        return getDocs(query(collection(courseRef, 'sections'), orderBy('order_index')));
      }),
    );

    // Coverage can differ by Mondai. Build the level exam list from the union so an
    // exam available in only one Mondai (for example N1 2021-07 in Mondai 4) is visible.
    const bySlug = new Map<string, ExamSummary>();
    for (const snap of sectionSnaps) {
      for (const sectionDoc of snap.docs) {
        const section = mapSection(sectionDoc.id, sectionDoc.data());
        if (!bySlug.has(section.slug)) {
          bySlug.set(section.slug, {
            slug: section.slug,
            title_vi: section.title_vi,
            order_index: 0,
          });
        }
      }
    }

    return Array.from(bySlug.values())
      .sort((a, b) => a.slug.localeCompare(b.slug))
      .map((exam, index) => ({ ...exam, order_index: index + 1 }));
  } catch (e) {
    rethrowContentError(e);
  }
}

export async function getExamMondaiList(
  level: Level,
  examSlug: string,
): Promise<{ level: Level; examSlug: string; items: ExamMondaiItem[] }> {
  try {
  const db = getDb();
  const mondaiNums: MondaiType[] = [1, 2, 3, 4, 5];
  const results = await Promise.all(
    mondaiNums.map(async (m) => {
      const courseSlug = courseSlugFor(level, m);
      const courseRef = doc(db, 'courses', courseSlug);
      const sectionRef = doc(courseRef, 'sections', examSlug);
      const [cSnap, sSnap] = await Promise.all([getDoc(courseRef), getDoc(sectionRef)]);
      if (!cSnap.exists() || !sSnap.exists()) return null;
      const course = mapCourse(cSnap.id, cSnap.data());
      const section = mapSection(sSnap.id, sSnap.data());
      return {
        mondai_type: course.mondai_type,
        mondai_name_jp: course.mondai_name_jp,
        mondai_name_vi: course.mondai_name_vi,
        courseSlug,
        question_count: section.question_count,
        options_per_question: course.options_per_question,
      } satisfies ExamMondaiItem;
    }),
  );
  const items = results.filter((x): x is ExamMondaiItem => x !== null);
  items.sort((a, b) => a.mondai_type - b.mondai_type);
  return { level, examSlug, items };
  } catch (e) {
    rethrowContentError(e);
  }
}

export async function getCourseBySlugWithTree(
  slug: string,
): Promise<CourseWithTree | null> {
  try {
  const db = getDb();
  const courseRef = doc(db, 'courses', slug);
  const courseDoc = await getDoc(courseRef);
  if (!courseDoc.exists()) return null;
  const course = mapCourse(courseDoc.id, courseDoc.data());

  const sectionsSnap = await getDocs(
    query(collection(courseRef, 'sections'), orderBy('order_index')),
  );

  // Skip fetching individual questions — section.question_count is denormalized
  // and sufficient for course tree view. Saves 22×14 doc reads.
  const sectionsWithQuestions: SectionWithQuestions[] = sectionsSnap.docs.map((sDoc) => {
    const section = mapSection(sDoc.id, sDoc.data());
    const stubQuestions: QuestionListItem[] = Array.from(
      { length: section.question_count },
      (_, i) => ({
        id: `q${String(i + 1).padStart(2, '0')}`,
        question_number: i + 1,
        order_index: i + 1,
      }),
    );
    return { ...section, questions: stubQuestions };
  });

  return { ...course, sections: sectionsWithQuestions };
  } catch (e) {
    rethrowContentError(e);
  }
}

export async function getSectionDetailBySlug(
  courseSlug: string,
  sectionSlug: string,
): Promise<SectionDetail | null> {
  try {
  const db = getDb();
  const courseRef = doc(db, 'courses', courseSlug);
  const sectionRef = doc(courseRef, 'sections', sectionSlug);

  const [courseDoc, sectionDoc] = await Promise.all([
    getDoc(courseRef),
    getDoc(sectionRef),
  ]);
  if (!courseDoc.exists() || !sectionDoc.exists()) return null;

  const course = mapCourse(courseDoc.id, courseDoc.data());
  const section = mapSection(sectionDoc.id, sectionDoc.data());

  const qsSnap = await getDocs(
    query(collection(sectionRef, 'questions'), orderBy('order_index')),
  );
  const questions: QuestionDoc[] = qsSnap.docs.map((d) => mapQuestion(d.id, d.data()));

  const audioUrls = questions.map((q) =>
    q.audio_path ? audioPublicUrl(q.audio_path) : '',
  );
  const imageUrls = questions.map((q) =>
    q.image_path ? audioPublicUrl(q.image_path) : null,
  );
  return {
    course,
    section,
    questions,
    audioUrl: section.audio_path ? audioPublicUrl(section.audio_path) : '',
    audioUrls,
    imageUrls,
  };
  } catch (e) {
    rethrowContentError(e);
  }
}

export async function getQuestionDetail(
  courseId: string,
  sectionId: string,
  questionId: string,
): Promise<QuestionDetail | null> {
  try {
  const db = getDb();
  const courseRef = doc(db, 'courses', courseId);
  const sectionRef = doc(courseRef, 'sections', sectionId);
  const questionRef = doc(sectionRef, 'questions', questionId);

  const [cSnap, sSnap, qSnap] = await Promise.all([
    getDoc(courseRef),
    getDoc(sectionRef),
    getDoc(questionRef),
  ]);
  if (!cSnap.exists() || !sSnap.exists() || !qSnap.exists()) return null;

  const course = mapCourse(cSnap.id, cSnap.data());
  const section = mapSection(sSnap.id, sSnap.data());
  const question = mapQuestion(qSnap.id, qSnap.data());

  // Compute prev/next within same section
  const siblings = await getDocs(
    query(collection(sectionRef, 'questions'), orderBy('order_index')),
  );
  const ids = siblings.docs.map((d) => d.id);
  const idx = ids.indexOf(questionId);
  const prev_question_id = idx > 0 ? ids[idx - 1] : null;
  const next_question_id = idx >= 0 && idx < ids.length - 1 ? ids[idx + 1] : null;

  return {
    ...question,
    section,
    course,
    prev_question_id,
    next_question_id,
  };
  } catch (e) {
    rethrowContentError(e);
  }
}
